import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AdminMe, LoginInput } from '@influence/contracts-admin'
import { authApi } from '@/api/auth.api'
import { setAccessToken } from '@/api/http'
import { setAbilities } from '@/plugins/casl'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AdminMe | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const role = computed(() => user.value?.role ?? null)

  function applyUser(u: AdminMe) {
    user.value = u
    setAbilities(u.abilities)
  }

  function clearUser() {
    user.value = null
    setAccessToken(null)
    setAbilities([])
  }

  /**
   * Восстановление сессии при заходе на admin-домен:
   * пробуем refresh по cookie, потом fetch /me.
   * Вызывается в main.ts перед монтированием.
   */
  async function restoreSession(): Promise<void> {
    if (initialized.value) return
    loading.value = true
    try {
      const refreshed = await authApi.refresh()
      if (!refreshed) {
        clearUser()
        return
      }
      const me = await authApi.me()
      applyUser(me)
    } catch {
      clearUser()
    } finally {
      initialized.value = true
      loading.value = false
    }
  }

  async function login(input: LoginInput): Promise<void> {
    loading.value = true
    try {
      const res = await authApi.login(input)
      const me = await authApi.me()
      applyUser({ ...me, ...res.user, abilities: me.abilities })
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      clearUser()
    }
  }

  return {
    user,
    loading,
    initialized,
    isAuthenticated,
    role,
    restoreSession,
    login,
    logout,
    clearUser,
    applyUser,
  }
})
